import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Check, Clock, Eraser, ShieldCheck, RotateCcw, History, Plus } from 'lucide-react';

const draftKeyFor = (templateId: string) => `doc_fill_draft_v1:${templateId}`;
const draftHasData = (d: any) =>
  !!d && ((d.values && Object.keys(d.values).length > 0) || (d.signatures && Object.keys(d.signatures).length > 0) || d.consent === true);
import {
  getDocumentTemplate,
  getProcedureTemplate,
  submitDocument,
  cryptoId,
  type DocumentBlock,
  type DocumentTemplateFull,
} from '../../lib/documents';

interface DocumentFillModalProps {
  templateId: string;
  onClose: () => void;
  onCompleted: (submissionId: string) => void;
  /** Link the resulting submission to a booking (counts toward the booking's required docs). */
  contextType?: 'standalone' | 'booking' | 'clock_event';
  contextId?: string | null;
}

export default function DocumentFillModal({ templateId, onClose, onCompleted, contextType, contextId }: DocumentFillModalProps) {
  const [doc, setDoc] = useState<DocumentTemplateFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState<Record<string, any>>({});
  const [signatures, setSignatures] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumed, setResumed] = useState(false);
  const draftKey = draftKeyFor(templateId);
  const hydratedRef = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Direct read works for business members; resources fall back to the
        // procedure RPC (their RLS hides document_templates/blocks).
        let full: DocumentTemplateFull | null = null;
        try {
          full = await getDocumentTemplate(templateId);
        } catch {
          full = null;
        }
        if (!full) {
          full = await getProcedureTemplate(templateId);
        }
        if (active) setDoc(full);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [templateId]);

  // Restore any saved progress for this form, then begin autosaving.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && typeof d === 'object') {
          if (d.values) setValues(d.values);
          if (d.signatures) setSignatures(d.signatures);
          if (typeof d.consent === 'boolean') setConsent(d.consent);
          if (draftHasData(d)) setResumed(true);
        }
      }
    } catch { /* ignore corrupt draft */ }
    hydratedRef.current = true;
  }, [draftKey]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({ values, signatures, consent, savedAt: Date.now() }));
    } catch { /* storage full / unavailable */ }
  }, [values, signatures, consent, draftKey]);

  // Seed table blocks with their preset rows so untouched presets are still submitted.
  useEffect(() => {
    if (!doc) return;
    setValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const b of doc.blocks) {
        if (b.type === 'table' && next[b.id] === undefined) {
          const cfgRows = Array.isArray(b.config?.rows) ? b.config.rows : [];
          next[b.id] = { rows: cfgRows.map((r: any) => ({ id: r.id || cryptoId(), cells: { ...(r.cells || {}) } })) };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [doc]);

  const clearDraft = () => { try { localStorage.removeItem(draftKey); } catch { /* noop */ } };

  const handleStartOver = () => {
    if (!confirm('Start over? Your saved progress on this form will be cleared.')) return;
    clearDraft();
    setValues({});
    setSignatures({});
    setConsent(false);
    setResumed(false);
    setError(null);
  };

  const setValue = (id: string, v: any) => setValues((prev) => ({ ...prev, [id]: v }));

  const requiresSignature = !!doc?.settings?.require_signature_to_submit || (doc?.blocks || []).some((b) => b.type === 'signature' && b.required);

  // Display-only blocks are never "fillable" and must never be validated as required.
  const DISPLAY_ONLY = ['heading', 'rich_text', 'divider', 'image'];

  const validate = (): string | null => {
    for (const b of doc?.blocks || []) {
      if (DISPLAY_ONLY.includes(b.type)) continue;
      if (!b.required) continue;
      if (b.type === 'signature') { if (!signatures[b.id]) return `Signature required: ${b.config?.label || 'Signature'}`; continue; }
      if (b.type === 'date' && b.config?.autoStampAtCompletion) continue;
      const v = values[b.id];
      if (b.type === 'checklist') {
        const items = b.config?.items || [];
        const allChecked = items.every((it: any) => v?.[it.id]);
        if (!allChecked) return `Complete all items: ${b.config?.label || 'Checklist'}`;
      } else if (b.type === 'multiple_choice') {
        if (!v || (Array.isArray(v) && v.length === 0)) return `Answer required: ${b.config?.label || 'Question'}`;
      } else if (b.type === 'acknowledge') {
        if (!v) return `Acknowledgement required`;
      } else if (b.type === 'table') {
        const rows = v?.rows || [];
        const hasContent = rows.some((r: any) => Object.values(r.cells || {}).some((x: any) => String(x ?? '').trim()));
        if (!hasContent) return `Fill in: ${b.config?.label || 'Table'}`;
      } else if (b.type === 'additional_signatures') {
        const entries = v?.entries || [];
        const ok = entries.some((e: any) => (e.name || '').trim() && e.signature_data);
        if (!ok) return `Add at least one signature: ${b.config?.label || 'Additional signatures'}`;
      } else if (v === undefined || v === '' || v === null) {
        return `Required: ${b.config?.label || b.type}`;
      }
    }
    if (!consent) return 'Please confirm the consent checkbox to submit.';
    return null;
  };

  const handleSubmit = async () => {
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const responses = (doc?.blocks || [])
        .filter((b) => !['heading', 'rich_text', 'divider', 'image', 'signature', 'additional_signatures'].includes(b.type))
        .map((b) => ({ block_id: b.id, block_type: b.type, value: values[b.id] ?? null }));
      const sigs: { block_id: string; signature_data: string; signer_name?: string }[] =
        Object.entries(signatures).map(([block_id, signature_data]) => ({ block_id, signature_data }));
      // Additional signatures — ad-hoc named signers added during completion.
      for (const b of (doc?.blocks || [])) {
        if (b.type !== 'additional_signatures') continue;
        const entries = (values[b.id]?.entries || []) as { name?: string; signature_data?: string }[];
        for (const e of entries) {
          if (e.signature_data) sigs.push({ block_id: b.id, signature_data: e.signature_data, signer_name: (e.name || '').trim() || undefined });
        }
      }
      const res = await submitDocument({ templateId, responses, signatures: sigs, consent, contextType, contextId });
      clearDraft();
      onCompleted(res.submissionId);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div className="fixed inset-0 z-[100050] bg-black/50 flex items-stretch justify-center sm:items-center sm:p-4" onClick={submitting ? undefined : onClose}>
      <div className="bg-gray-100 shadow-xl w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[92vh] flex flex-col rounded-none sm:rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white sm:rounded-t-2xl shrink-0" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
          <div className="min-w-0">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">Complete document</div>
            <div className="text-[15px] font-semibold text-gray-900 truncate">{doc?.title || 'Document'}</div>
          </div>
          <button onClick={onClose} disabled={submitting} title="Close — your progress is saved" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : !doc ? (
            <div className="text-center py-16 text-gray-400 text-sm">Document not found.</div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 max-w-xl mx-auto p-6 sm:p-8 space-y-5">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{doc.title}</h1>
                {doc.description && <p className="text-[14px] text-gray-500 mt-1.5">{doc.description}</p>}
              </div>
              {resumed && (
                <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 text-[13px] text-blue-700"><History className="h-4 w-4" /> Resumed your saved progress</div>
                  <button onClick={handleStartOver} className="inline-flex items-center gap-1 text-[12px] font-medium text-blue-700 hover:text-blue-900"><RotateCcw className="h-3.5 w-3.5" /> Start over</button>
                </div>
              )}
              <div className="h-px bg-gray-100" />
              {doc.blocks.map((b) => (
                <FillBlock
                  key={b.id}
                  block={b}
                  value={values[b.id]}
                  signature={signatures[b.id]}
                  onValue={(v) => setValue(b.id, v)}
                  onSignature={(d) => setSignatures((prev) => ({ ...prev, [b.id]: d }))}
                />
              ))}

              {/* Consent */}
              <div className="pt-2 border-t border-gray-100">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600" />
                  <span className="text-[13px] text-gray-700">
                    {requiresSignature
                      ? 'I agree to sign and complete this document electronically, and confirm the information is accurate.'
                      : 'I confirm the information above is accurate and complete.'}
                  </span>
                </label>
              </div>

              {error && <div className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-white sm:rounded-b-2xl flex items-center justify-between shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-[11px] text-gray-400"><ShieldCheck className="h-3.5 w-3.5" /> Locked & timestamped on submit</div>
            {(Object.keys(values).length > 0 || Object.keys(signatures).length > 0 || consent) && (
              <button onClick={handleStartOver} disabled={submitting} className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 hover:text-red-600 disabled:opacity-50"><RotateCcw className="h-3.5 w-3.5" /> Start over</button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={submitting} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-[13px] font-medium hover:bg-gray-50 disabled:opacity-50">Close</button>
            <button onClick={handleSubmit} disabled={submitting || loading} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700 disabled:opacity-60">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <><Check className="h-4 w-4" /> Complete & submit</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function Req({ b }: { b: DocumentBlock }) { return b.required ? <span className="text-red-500 ml-0.5">*</span> : null; }

function FillBlock({ block, value, signature, onValue, onSignature }: {
  block: DocumentBlock; value: any; signature?: string; onValue: (v: any) => void; onSignature: (d: string) => void;
}) {
  const c = block.config || {};
  const inputCls = 'w-full text-[14px] border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30';

  switch (block.type) {
    case 'heading': {
      const size = c.size === 'h1' ? 'text-2xl' : c.size === 'h3' ? 'text-base' : 'text-xl';
      return <div className={`${size} font-bold text-gray-900`}>{c.text}</div>;
    }
    case 'rich_text':
      return <p className="text-[14px] text-gray-700 whitespace-pre-wrap leading-relaxed">{c.text}</p>;
    case 'divider':
      return <hr className="border-gray-200" />;
    case 'image':
      return c.url ? <img src={c.url} alt={c.caption || ''} className="max-h-64 rounded-lg" /> : null;
    case 'checklist':
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-2">{c.label}<Req b={block} /></div>}
          <div className="space-y-1.5">
            {(c.items || []).map((it: any) => (
              <label key={it.id} className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={!!value?.[it.id]} onChange={(e) => onValue({ ...(value || {}), [it.id]: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                <span className="text-[14px] text-gray-700">{it.text}</span>
              </label>
            ))}
          </div>
        </div>
      );
    case 'acknowledge':
      return (
        <label className="flex items-start gap-2.5 cursor-pointer">
          <input type="checkbox" checked={!!value} onChange={(e) => onValue(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600" />
          <span className="text-[14px] text-gray-700">{c.label}<Req b={block} /></span>
        </label>
      );
    case 'multiple_choice': {
      const selected: string[] = Array.isArray(value) ? value : value ? [value] : [];
      const toggle = (id: string) => {
        if (c.multiple) {
          onValue(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
        } else onValue([id]);
      };
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-2">{c.label}<Req b={block} /></div>
          <div className="space-y-1.5">
            {(c.options || []).map((opt: any) => (
              <label key={opt.id} className="flex items-center gap-2.5 cursor-pointer">
                <input type={c.multiple ? 'checkbox' : 'radio'} checked={selected.includes(opt.id)} onChange={() => toggle(opt.id)} className="h-4 w-4 border-gray-300 text-blue-600" />
                <span className="text-[14px] text-gray-700">{opt.text}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    case 'short_text':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>
          <input value={value || ''} onChange={(e) => onValue(e.target.value)} placeholder={c.placeholder} className={inputCls} style={{ fontSize: '16px' }} />
        </div>
      );
    case 'long_text':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>
          <textarea value={value || ''} onChange={(e) => onValue(e.target.value)} placeholder={c.placeholder} rows={3} className={`${inputCls} resize-y`} style={{ fontSize: '16px' }} />
        </div>
      );
    case 'number':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>
          <div className="flex items-center gap-2">
            <input type="number" value={value ?? ''} onChange={(e) => onValue(e.target.value === '' ? null : Number(e.target.value))} className={`${inputCls} w-40`} style={{ fontSize: '16px' }} />
            {c.unit && <span className="text-[13px] text-gray-500">{c.unit}</span>}
          </div>
        </div>
      );
    case 'date':
      if (c.autoStampAtCompletion) {
        return (
          <div>
            <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>
            <div className="inline-flex items-center gap-1.5 text-[13px] text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2"><Clock className="h-4 w-4" /> Stamped automatically when you submit</div>
          </div>
        );
      }
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>
          <input type="date" value={value || ''} onChange={(e) => onValue(e.target.value)} className={`${inputCls} w-48`} style={{ fontSize: '16px' }} />
        </div>
      );
    case 'time':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>
          <input type="time" value={value || ''} onChange={(e) => onValue(e.target.value)} className={`${inputCls} w-40`} style={{ fontSize: '16px' }} />
        </div>
      );
    case 'signature':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>
          <SignaturePad value={signature} onChange={onSignature} />
        </div>
      );
    case 'file_upload':
    case 'photo':
      return (
        <div>
          <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>
          <input
            type="file"
            accept={block.type === 'photo' ? 'image/*' : undefined}
            onChange={(e) => onValue(e.target.files?.[0]?.name || null)}
            className="text-[13px]"
          />
          <p className="text-[11px] text-gray-400 mt-1">File name is recorded with this submission.</p>
        </div>
      );
    case 'table': {
      const columns: { id: string; label: string }[] = Array.isArray(c.columns) ? c.columns : [];
      const rows: { id: string; cells: Record<string, string> }[] = value?.rows
        || (Array.isArray(c.rows) ? c.rows.map((r: any) => ({ id: r.id, cells: { ...(r.cells || {}) } })) : []);
      const setCell = (rowIdx: number, colId: string, val: string) => {
        onValue({ rows: rows.map((r, j) => (j === rowIdx ? { ...r, cells: { ...r.cells, [colId]: val } } : r)) });
      };
      const addRow = () => onValue({ rows: [...rows, { id: cryptoId(), cells: {} }] });
      const removeRow = (id: string) => onValue({ rows: rows.filter((r) => r.id !== id) });
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>}
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  {columns.map((col) => (
                    <th key={col.id} className="border border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700 min-w-[110px]">{col.label}</th>
                  ))}
                  {c.allowAddRows !== false && rows.length > 1 && <th className="border border-gray-200 w-8" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rIdx) => (
                  <tr key={row.id}>
                    {columns.map((col) => (
                      <td key={col.id} className="border border-gray-200 p-1 align-top">
                        <textarea
                          value={row.cells?.[col.id] || ''}
                          onChange={(e) => setCell(rIdx, col.id, e.target.value)}
                          rows={1}
                          className="w-full text-[13px] text-gray-900 px-1.5 py-1 outline-none resize-y bg-transparent"
                          style={{ fontSize: '16px' }}
                        />
                      </td>
                    ))}
                    {c.allowAddRows !== false && rows.length > 1 && (
                      <td className="border border-gray-200 text-center align-middle">
                        <button type="button" onClick={() => removeRow(row.id)} className="p-1 text-gray-300 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {c.allowAddRows !== false && (
            <button type="button" onClick={addRow} className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-blue-600 hover:text-blue-800">+ Add row</button>
          )}
        </div>
      );
    }
    case 'additional_signatures': {
      const entries: { id: string; name?: string; signature_data?: string }[] = Array.isArray(value?.entries) ? value.entries : [];
      const setEntry = (i: number, patch: any) => onValue({ entries: entries.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
      const addEntry = () => onValue({ entries: [...entries, { id: cryptoId(), name: '', signature_data: '' }] });
      const removeEntry = (id: string) => onValue({ entries: entries.filter((e) => e.id !== id) });
      return (
        <div>
          {c.label && <div className="text-[14px] font-semibold text-gray-800 mb-1.5">{c.label}<Req b={block} /></div>}
          <div className="space-y-3">
            {entries.map((e, i) => (
              <div key={e.id} className="rounded-lg border border-gray-200 p-3 bg-gray-50/40">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[12px] font-medium text-gray-500">Signer {i + 1}</span>
                  <button type="button" onClick={() => removeEntry(e.id)} className="text-gray-300 hover:text-red-500"><X className="h-4 w-4" /></button>
                </div>
                <input
                  value={e.name || ''}
                  onChange={(ev) => setEntry(i, { name: ev.target.value })}
                  placeholder="Full name"
                  className="w-full mb-2 text-[14px] border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  style={{ fontSize: '16px' }}
                />
                <SignaturePad value={e.signature_data} onChange={(d) => setEntry(i, { signature_data: d })} />
              </div>
            ))}
          </div>
          <button type="button" onClick={addEntry} className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-blue-600 hover:text-blue-800">
            <Plus className="h-4 w-4" /> Add signature
          </button>
        </div>
      );
    }
    default:
      return null;
  }
}

function SignaturePad({ value, onChange }: { value?: string; onChange: (d: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#111827';
    // Redraw a restored signature (e.g. resumed draft) once onto the canvas.
    if (value && !restoredRef.current) {
      restoredRef.current = true;
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  }, [value]);

  const pos = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const start = (e: React.PointerEvent) => { drawing.current = true; last.current = pos(e); (e.target as HTMLElement).setPointerCapture(e.pointerId); };
  const move = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current!.x, last.current!.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(canvasRef.current!.toDataURL('image/png')); } };
  const clear = () => { const ctx = canvasRef.current!.getContext('2d')!; ctx.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height); restoredRef.current = false; onChange(''); };

  return (
    <div>
      <div className="relative rounded-lg border-2 border-dashed border-gray-300 bg-white">
        <canvas
          ref={canvasRef}
          width={520}
          height={140}
          className="w-full touch-none"
          style={{ height: 140 }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        {!value && <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-gray-300">Sign here</div>}
      </div>
      <button type="button" onClick={clear} className="mt-1.5 inline-flex items-center gap-1 text-[12px] text-gray-500 hover:text-gray-700"><Eraser className="h-3.5 w-3.5" /> Clear</button>
    </div>
  );
}
